<template>
  <validation-observer
    v-if="isReady"
    ref="updateCustomRepeat"
    #default="{ invalid }"
  >
    <n-form-confirmation
      key="updateCustomRepeat"
      @submit="validateForm"
      :bypass="true"
      :form="$refs.updateCustomRepeat"
      :disabled="invalid"
    >
      <b-card-title class="mb-1">{{ $t('label.customRepeat') }}</b-card-title>

      <n-input :fields="fields" v-model="model" />

      <div class="multi-checkbox d-flex flex-wrap">
        <div>
          <b-form-checkbox
            v-for="(label, idx) in byOptions"
            :key="idx"
            v-model="byOptionModel[repeatFrequency][idx]"
            button
            button-variant="outline-primary"
            size="sm"
            :disabled="
              selectedByOptionsCount === 1 &&
              byOptionModel[repeatFrequency][idx]
            "
          >
            {{ $t(label) }}
          </b-form-checkbox>
        </div>
      </div>

      <b-row>
        <b-col cols="12" class="d-flex items-center justify-content-end">
          <b-button
            variant="secondary"
            class="close-btn mt-1 mr-2"
            @click="handleCancel"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <n-button-loading
            type="submit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-1"
            :loading="loading"
            :disabled="invalid"
          >
            {{ $t('button.save') }}
          </n-button-loading>
        </b-col>
      </b-row>
    </n-form-confirmation>
  </validation-observer>
</template>

<script>
  import {
    BPopover,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormCheckbox,
    BCardTitle
  } from 'bootstrap-vue';
  import FormInput from './updateCustomRepeat';
  import NButtonLoading from '@/components/NButtonLoading';
  import NFormConfirmation from '@/components/NFormConfirmation';
  import NInput from '@/components/NInput';
  import Ripple from 'vue-ripple-directive';

  const FREQ_META = {
    daily: [],
    weekly: [],
    monthly: [],
    yearly: []
  };

  const freqMap = {
    daily: 'none',
    weekly: 'byweekday',
    monthly: 'bymonthday',
    yearly: 'bymonth'
  };

  export default {
    name: 'TimeShiftDropdown',
    components: {
      BPopover,
      BRow,
      BCol,
      BCard,
      BButton,
      BFormGroup,
      BFormCheckbox,
      NButtonLoading,
      NFormConfirmation,
      NInput,
      BCardTitle
    },
    props: {
      activeDate: {
        type: Date,
        default: null
      },
      event: {
        type: Object,
        default: () => {}
      }
    },
    directives: {
      Ripple
    },
    data() {
      return {
        isReady: false,
        model: {
          repeatFrequency: {
            value: 'daily',
            name: this.$t('label.daily')
          },
          repeatInterval: 1
        },
        freqMeta: { ...FREQ_META },
        byOptionModel: { ...FREQ_META },
        loading: false
      };
    },
    setup() {
      const fields = FormInput;
      return {
        fields
      };
    },
    computed: {
      repeatFrequency() {
        return this.model['repeatFrequency']?.value || 'daily';
      },
      byOptions() {
        return this.freqMeta[this.repeatFrequency] || [];
      },
      selectedByOptions() {
        return this.byOptionModel[this.repeatFrequency] || [];
      },
      selectedByOptionsCount() {
        return this.selectedByOptions.filter(Boolean).length;
      }
    },
    created() {
      this.initData();
    },
    methods: {
      initData() {
        this.initFreqMeta();

        // Mean it's already made custom
        if (this.event.isCustom) {
          const { rrule } = this.event;
          const freq = rrule.freq;
          const byOptions = rrule[freqMap[freq]];

          // Restore form modal
          this.model.repeatFrequency = {
            ...FormInput.find(e => e.key === 'repeatFrequency').options.find(
              e => e.value === freq
            )
          };

          this.model.repeatInterval = rrule.interval;

          if (freq !== 'daily') {
            this.byOptionModel[freq] = this.freqMeta[freq].map((_, idx) =>
              byOptions.includes(this.getFreqIdx(freq, idx))
            );
          }
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.isReady = true;
          }, 100);
        });
      },
      initFreqMeta() {
        const weekly = [...Array(7)].map((_, i) =>
          new Intl.DateTimeFormat(this.$i18n.locale, {
            weekday: 'short'
          }).format(new Date(Date.UTC(1990, 0, i + 1)))
        );

        const monthly = [...Array(31)].map((_, i) => i + 1);

        const yearly = [...Array(12)].map((_, i) =>
          new Intl.DateTimeFormat(this.$i18n.locale, { month: 'short' }).format(
            new Date(Date.UTC(1990, i, 1))
          )
        );

        this.freqMeta = {
          weekly,
          monthly,
          yearly
        };

        const mockByOption = {
          byweekday: this.activeDate.toLocaleString(this.$i18n.locale, {
            weekday: 'short'
          }),
          bymonthday: this.activeDate.getDate(),
          bymonth: this.activeDate.toLocaleString(this.$i18n.locale, {
            month: 'short'
          })
        };

        for (const freq in freqMap) {
          this.byOptionModel[freq] = (this.freqMeta[freq] || []).map(
            e => e == mockByOption[freqMap[freq]]
          );
        }
      },
      getFreqIdx(freq, idx) {
        return freq == 'weekly' ? idx : idx + 1;
      },
      validateForm() {
        this.$refs.updateCustomRepeat.validate().then(success => {
          if (success) {
            this.submit();
          }
        });
      },
      submit() {
        const { repeatInterval } = this.model;
        const freq = this.repeatFrequency;

        // Filter selected by options
        const byOptions = this.byOptionModel[freq]
          .map((selected, idx) => {
            return selected ? this.getFreqIdx(freq, idx) : -1;
          })
          .filter(e => e > -1);

        const byOption = {};

        // Remove other byoption fields
        for (const key in freqMap) {
          if (key == 'daily') continue;
          byOption[freqMap[key]] = key == freq ? byOptions : undefined;
        }

        // Prepare payload
        const payload = {
          freq,
          interval: parseInt(repeatInterval),
          ...byOption
        };

        this.$emit('save', payload);
      },

      handleCancel() {
        this.$emit('cancel');
      }
    }
  };
</script>

<style lang="scss">
  .multi-checkbox {
    .btn {
      width: 45px;
      padding: 8px !important;
      border-radius: 0;
      font-family: Menlo, Monaco, Lucida Console, Liberation Mono,
        DejaVu Sans Mono, Bitstream Vera Sans Mono, Courier New, monospace,
        sans-serif;
    }
  }
</style>
